import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

import noResultsImage from "/asset/image/noResultsFound.png";
import useInsurance from "/b2c/query/useInsurance.b2c";
import { AlertCircleOpen } from "/component/base/Icon";
import List from "/component/base/List";
import Loading from "/component/base/Loading";
import Text from "/component/base/Text";
import MainContent from "/component/layout/MainContent";
import EmptyStateCta from "/component/partial/EmptyStateCta";
import { useTranslation } from "/hook";
import { useAnalytics } from "/hook/useAnalytics/useAnalytics";
import { layout } from "/styles";
import { reactPlugin } from "/util/appInsights.util";

import { ListContainer, ListWrapper } from "./InsuranceB2C.styles";
import {
  convertToPolicyOrderString,
  getFilteredInsurances,
  isInsuranceExpired,
} from "./InsuranceB2C.utils";
import InsuranceItem from "./InsuranceItem";

const IMAGE_HEIGHT = 261;
const IMAGE_WIDTH = 183;

const Insurance = () => {
  const { t } = useTranslation("insurance");
  const { data, error, loading } = useInsurance();
  const { recordAnalyticsEvent } = useAnalytics();
  const insurances = getFilteredInsurances(data?.patient.insurances || []);

  useEffect(() => {
    if (!loading && !error) {
      recordAnalyticsEvent("viewInsurances", {
        insuranceCount: insurances.length ?? 0,
        inactive: insurances?.filter((item) => isInsuranceExpired(item)).length ?? 0,
      });
    }
  }, [recordAnalyticsEvent, insurances, loading, error]);

  if (error) {
    return (
      <MainContent pageTitle={t("title")} parentPage="account">
        <Helmet>
          <title>{t("title")}</title>
        </Helmet>
        <div css={layout.flexVerticalCenter}>
          <div css={layout.marginVertical(80)}>
            <EmptyStateCta
              icon={<AlertCircleOpen color="brandPrimary" size={48} />}
              title={t("error.fetchError.title")}
              subtitle={t("error.fetchError.subtitle")}
            />
          </div>
        </div>
      </MainContent>
    );
  }

  return (
    <MainContent parentPage="account" pageTitle={t("title")}>
      <Helmet>
        <title>{t("title")}</title>
      </Helmet>

      {loading && <Loading />}

      {!loading &&
        (insurances.length === 0 ? (
          <div css={layout.flexVerticalCenter}>
            <div css={layout.marginVertical(80)}>
              <EmptyStateCta
                imageHeight={IMAGE_HEIGHT}
                imageWidth={IMAGE_WIDTH}
                imageSource={noResultsImage}
                title={t("emptyStateTitle")}
                subtitle={t("emptyStateSubtitle")}
              />
            </div>
          </div>
        ) : (
          <>
            <Text>{t("subtitle")}</Text>
            <ListWrapper>
              <ListContainer>
                <List
                  as="Divs"
                  showChevron={false}
                  data={insurances}
                  hideLastChildBorder
                  renderItem={(item, index) => (
                    <InsuranceItem
                      insurancePlanName={item.insurancePlanName}
                      memberId={item.insuranceIdNumber}
                      groupNumber={item.policyNumber}
                      dateLastVerified={item.lastUpdated}
                      insuranceType={convertToPolicyOrderString(t, item.sequenceNumber)}
                      index={index}
                      eligibilityStatus={item.eligibilityStatus}
                      inactive={isInsuranceExpired(item)}
                    />
                  )}
                />
              </ListContainer>
            </ListWrapper>
          </>
        ))}
    </MainContent>
  );
};

export default withAITracking(reactPlugin, Insurance, undefined, "ai-tracking");
